import { useState, useEffect } from 'react';
import axios from 'axios';
import { ExternalLink, X } from 'lucide-react';
import { getImageUrl } from '../utils/imageUrl';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BannerDisplay - Shows active banners from the database
 * Positions: 'top', 'sidebar', 'between-sections'
 */
export const BannerDisplay = ({ position = 'top', className = '' }) => {
  const { t } = useLanguage();
  const [banners, setBanners] = useState([]);
  const [dismissedBanners, setDismissedBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBanners();
    // Load dismissed banners from session storage
    const dismissed = sessionStorage.getItem('dismissedBanners');
    if (dismissed) {
      setDismissedBanners(JSON.parse(dismissed));
    }
  }, [position]);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API_URL}/banners`, {
        params: { position }
      });
      setBanners(response.data);
    } catch (error) {
      console.error('Failed to fetch banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBannerClick = async (banner) => {
    // Track click
    try {
      await axios.post(`${API_URL}/banners/${banner.id}/click`);
    } catch (error) {
      console.error('Failed to track banner click:', error);
    }
    
    // Open link
    if (banner.link_url) {
      window.open(banner.link_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDismiss = (bannerId) => {
    const newDismissed = [...dismissedBanners, bannerId];
    setDismissedBanners(newDismissed);
    sessionStorage.setItem('dismissedBanners', JSON.stringify(newDismissed));
  };

  const visibleBanners = banners.filter(b => !dismissedBanners.includes(b.id));

  if (loading || visibleBanners.length === 0) {
    return null;
  }

  // Top banner style
  if (position === 'top') {
    return (
      <div className={`space-y-3 ${className}`}>
        {visibleBanners.map(banner => (
          <div 
            key={banner.id}
            data-testid={`banner-${banner.id}`}
            className="relative bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 border border-white/10 rounded-xl overflow-hidden group"
          >
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => handleBannerClick(banner)}
            >
              <div className="flex items-center gap-4">
                {banner.image_url && (
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-white/10">
                    <img 
                      src={getImageUrl(banner.image_url)} 
                      alt={banner.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div>
                  <h4 className="font-heading text-lg text-cream group-hover:text-white transition-colors">
                    {banner.title}
                  </h4>
                  {banner.link_url && (
                    <div className="flex items-center gap-1 text-cream-muted text-sm mt-1">
                      <ExternalLink className="w-3 h-3" />
                      <span className="truncate max-w-[200px]">{new URL(banner.link_url).hostname}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
                  Annonce
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDismiss(banner.id);
                  }}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-cream-muted hover:text-cream"
                  title={t('gen.d697101c56')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Sidebar style
  if (position === 'sidebar') {
    return (
      <div className={`space-y-4 ${className}`}>
        {visibleBanners.map(banner => (
          <div 
            key={banner.id}
            data-testid={`banner-sidebar-${banner.id}`}
            className="relative bg-zinc-900/50 border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:border-white/20 transition-all"
            onClick={() => handleBannerClick(banner)}
          >
            {banner.image_url && (
              <div className="aspect-video w-full overflow-hidden">
                <img 
                  src={getImageUrl(banner.image_url)} 
                  alt={banner.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                  Annonce
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDismiss(banner.id);
                  }}
                  className="p-1 hover:bg-white/10 rounded transition-colors text-cream-muted hover:text-cream"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <h4 className="font-heading text-base text-cream mb-1">{banner.title}</h4>
              {banner.link_url && (
                <div className="flex items-center gap-1 text-cream-muted text-xs">
                  <ExternalLink className="w-3 h-3" />
                  <span>{t('clubs.visitSite')}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Between sections style
  if (position === 'between-sections') {
    return (
      <div className={`${className}`}>
        {visibleBanners.map(banner => (
          <div 
            key={banner.id}
            data-testid={`banner-section-${banner.id}`}
            className="relative bg-gradient-to-r from-zinc-900/80 via-zinc-800/80 to-zinc-900/80 backdrop-blur border border-white/10 rounded-2xl overflow-hidden cursor-pointer hover:border-white/20 transition-all"
            onClick={() => handleBannerClick(banner)}
          >
            <div className="flex flex-col md:flex-row items-center gap-6 p-6">
              {banner.image_url && (
                <div className="w-full md:w-48 h-32 rounded-xl overflow-hidden flex-shrink-0 border border-white/10">
                  <img 
                    src={getImageUrl(banner.image_url)} 
                    alt={banner.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
                    Annonce
                  </span>
                </div>
                <h4 className="font-heading text-xl text-cream mb-2">{banner.title}</h4>
                {banner.link_url && (
                  <button className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-cream text-sm transition-colors">
                    <ExternalLink className="w-4 h-4" /> {t('gen.0f1bf9d32c')}
                  </button>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDismiss(banner.id);
                }}
                className="absolute top-3 right-3 p-2 hover:bg-white/10 rounded-lg transition-colors text-cream-muted hover:text-cream"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export default BannerDisplay;
